import { cn } from '@endure/ui';
import { matchPath, useLocation } from 'react-router';

import { AccordionAnimatedContent, Dropdown, Icon } from 'components';
import { Link } from 'containers/Link';
import { useTranslation } from 'libs/translations';
import { useState } from 'react';
import type { MenuItem as MenuItemType, SubMenu } from '../types';
import { SubMenuContent } from './SubMenuContent';
import type { SubMenuItemProps } from './SubMenuContent/SubMenuItem';

export interface MenuItemProps {
  item: MenuItemType | SubMenu;
  onClick: () => void;
  variant?: 'dark' | 'light';
}

export const MenuItem: React.FC<MenuItemProps> = ({ item, onClick, variant = 'dark' }) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { pathname } = useLocation();

  let isActive = false;

  if ('to' in item && item.to) {
    isActive = !!matchPath({ path: item.to, end: false }, pathname);
  } else if ('items' in item) {
    isActive = item.items.some((i) => i.to && matchPath({ path: i.to, end: false }, pathname));
  }

  const sharedContainerClassName = cn(
    'forge-nav-menu-item block w-full text-left py-3 font-semibold transition-colors hover:no-underline xl:font-normal xl:px-4 xl:py-3 xl:rounded-full xl:whitespace-nowrap',
    'text-muted hover:text-foreground active:text-foreground xl:hover:bg-surface-2',
    isActive && 'forge-nav-menu-item--active xl:bg-surface-2 xl:text-foreground',
  );

  // Toggle menu's open state
  const onAccordionTriggerClick = () => setIsSubMenuOpen((value) => !value);

  return 'items' in item ? (
    <div>
      {/* XL and up backdrop */}
      {isSubMenuOpen && (
        <button
          aria-label="Close menu"
          className="fixed bottom-0 left-0 right-0 top-0 hidden z-50 xl:block"
          onClick={() => setIsSubMenuOpen(false)}
          type="button"
        />
      )}

      {/* Mobile/tablet submenu */}
      <div className="xl:hidden">
        <button
          className={cn(
            sharedContainerClassName,
            'flex items-center justify-between cursor-pointer xl:gap-x-2',
          )}
          type="button"
          onClick={onAccordionTriggerClick}
        >
          <span className="whitespace-nowrap">{item.label}</span>

          <Icon
            name="chevronDown"
            className={cn('size-3 shrink-0', isSubMenuOpen && 'rotate-180')}
          />
        </button>

        <AccordionAnimatedContent isOpen={isSubMenuOpen} className="pb-3 xl:hidden">
          <SubMenuContent
            {...item}
            chromeVariant={variant}
            items={item.items.map((i) => ({
              ...i,
              onClick: () => {
                // Close submenu
                setIsSubMenuOpen(false);

                onClick();
              },
            }))}
          />
        </AccordionAnimatedContent>
      </div>

      {/* XL and up dropdown */}
      <Dropdown
        className="hidden xl:block"
        menuClassName={cn('forge-nav-dropdown top-7 border border-line bg-elevated shadow-none')}
        optionsDom={({ setIsDropdownOpened }) => {
          const items: SubMenuItemProps[] = item.items.map((i) => ({
            ...i,
            onClick: () => {
              // Close dropdown
              setIsDropdownOpened(false);

              onClick();
            },
          }));

          return <SubMenuContent {...item} items={items} chromeVariant={variant} />;
        }}
      >
        {({ isDropdownOpened, handleToggleDropdown }) => (
          <button
            className={cn(
              sharedContainerClassName,
              'flex items-center justify-between cursor-pointer xl:gap-x-2',
              isDropdownOpened && 'forge-nav-menu-item--active xl:bg-surface-2 xl:text-foreground',
            )}
            type="button"
            onClick={handleToggleDropdown}
          >
            <span>{item.label}</span>

            <Icon name="chevronDown" className={cn('size-3', isDropdownOpened && 'rotate-180')} />
          </button>
        )}
      </Dropdown>
    </div>
  ) : (
    <Link
      to={item.to}
      href={item.href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(sharedContainerClassName, 'flex items-center gap-x-2')}
    >
      <span>{item.label}</span>

      {item.isBeta && (
        <div
          className={cn(
            'forge-nav-beta rounded-full bg-accent px-1.5 py-0.5 text-b2s text-inverse',
          )}
        >
          {t('layout.menu.beta')}
        </div>
      )}
    </Link>
  );
};
